import { cleanFloat } from "@/lib/tool-kit/grades/rounding";

/**
 * ECTS, US semester credit hours and UK credits.
 *
 * Sources (retrieved October 2026):
 * - European Commission, "ECTS Users' Guide" (2015), "ECTS key features" and the glossary entry
 *   "Workload": 60 ECTS credits are allocated to a full-time academic year; in most cases a
 *   year's workload is 1,500 to 1,800 hours, so one credit corresponds to 25 to 30 hours.
 *   https://education.ec.europa.eu/sites/default/files/document-library-docs/ects-users-guide_en.pdf
 *   A semester is half a year and so 30 credits by arithmetic; the guide itself says "60
 *   credits per full-time academic year".
 * - QAA, "Higher Education Credit Framework for England: Advice on Academic Credit
 *   Arrangements" (second edition, May 2021): one UK credit is typically 10 notional hours of
 *   learning, a typical year is 120 credits, and "In everyday practice, two UK credits are
 *   equivalent to one ECTS credit".
 *   https://www.qaa.ac.uk/docs/qaa/quality-code/higher-education-credit-framework-for-england.pdf
 * - US federal credit hour, 34 CFR 600.2: one semester hour reasonably approximates not less
 *   than one hour of classroom instruction plus two hours of out-of-class work each week for
 *   about fifteen weeks, so 15 × 3 = 45 hours.
 * - ECTS to US credit: there is no fixed rule. Michigan multiplies ECTS by 0.5 (Michigan
 *   Technology Services knowledge base, "European Credit (ECTS) Conversion to U-M Credit"),
 *   TCNJ states "2 ECTS credits = 1 US credit" with 5 ECTS = 2.5 US credits (Center for Global
 *   Engagement, "Credits, Contact Hours, and Units Abroad"), Washington and Lee counts 2 ECTS as
 *   1 credit but awards no fractional credit (University Registrar, "Credit from Away
 *   Policies"), and the University of North Florida awards 3 credits for 4 to 7.5 ECTS and
 *   otherwise ECTS ÷ 2 with partial credits rounded up (Coggin College, "Exchange Program
 *   Credit Conversions"). The receiving institution decides, so the ratio and the rounding
 *   are inputs.
 */

export type CreditUnit = "ects" | "us" | "uk";
export type RoundingPolicy = "exact" | "up" | "down";

export const ECTS_PER_YEAR = 60;
export const UK_CREDITS_PER_ECTS = 2;
export const DEFAULT_ECTS_PER_US_CREDIT = 2;
export const ECTS_HOURS_MIN = 25;
export const ECTS_HOURS_MAX = 30;
export const UK_HOURS_PER_CREDIT = 10;
/** One US semester hour: 1 hour in class + 2 out of class, for 15 weeks. */
export const US_HOURS_PER_SEMESTER_CREDIT = 45;

export interface CreditResult {
  ects: number;
  us: number;
  uk: number;
}

/**
 * Converts an amount in one unit to all three. `ectsPerUs` is how many ECTS credits the
 * receiving institution treats as one US semester credit. Null for a non-positive ratio.
 */
export function convertCredits(
  value: number,
  from: CreditUnit,
  ectsPerUs: number
): CreditResult | null {
  if (!(ectsPerUs > 0) || value < 0 || !Number.isFinite(value)) {
    return null;
  }
  let ects: number;
  if (from === "ects") {
    ects = value;
  } else if (from === "us") {
    ects = value * ectsPerUs;
  } else {
    ects = value / UK_CREDITS_PER_ECTS;
  }
  return {
    ects: cleanFloat(ects),
    us: cleanFloat(ects / ectsPerUs),
    uk: cleanFloat(ects * UK_CREDITS_PER_ECTS),
  };
}

/** Applies a whole-credit rounding policy to a US credit amount. */
export function roundUsCredits(value: number, policy: RoundingPolicy): number {
  const clean = cleanFloat(value);
  if (policy === "up") {
    return Math.ceil(clean);
  }
  if (policy === "down") {
    return Math.floor(clean);
  }
  return clean;
}

export interface Workload {
  /** ECTS credits × 25 to × 30 hours. */
  ectsMin: number;
  ectsMax: number;
  /** UK credits × 10 notional hours. */
  uk: number;
  /** US credits × 45 hours (the federal benchmark). */
  us: number;
}

export function workloadHours(credits: CreditResult): Workload {
  return {
    ectsMin: cleanFloat(credits.ects * ECTS_HOURS_MIN),
    ectsMax: cleanFloat(credits.ects * ECTS_HOURS_MAX),
    uk: cleanFloat(credits.uk * UK_HOURS_PER_CREDIT),
    us: cleanFloat(credits.us * US_HOURS_PER_SEMESTER_CREDIT),
  };
}

/** Share of a 60-ECTS academic year, as a fraction (30 ECTS → 0.5). */
export function shareOfYear(ects: number): number {
  return cleanFloat(ects / ECTS_PER_YEAR);
}
